import { useState, useEffect, useCallback } from 'react'
import { getSupabase } from './useAuth'
import { apiFetch } from '@/lib/api'
import { API_BASE } from '@/lib/config'

export interface UserProfile {
  id: string
  email?: string | null
  display_name: string | null
  avatar_url: string | null
  role?: string | null
  isAdmin?: boolean
  plan: 'free' | 'pro'
  credits: number
  referral_code: string | null
  stripe_customer_id: string | null
  created_at: string
}

export interface CreditTransaction {
  id: string
  amount: number
  type: string
  description: string | null
  created_at: string
}

const DEV_PROFILE: UserProfile = {
  id: 'dev-user-local',
  email: 'dev@sine.no',
  display_name: 'Dev',
  avatar_url: null,
  role: 'admin',
  isAdmin: true,
  plan: 'pro',
  credits: 1000,
  referral_code: 'DEVCODE1',
  stripe_customer_id: null,
  created_at: new Date().toISOString(),
}

const PROFILE_CACHE_TTL_MS = 10000
const profileCache = new Map<string, { value: UserProfile; fetchedAt: number }>()
const profileInflight = new Map<string, Promise<UserProfile | null>>()
const transactionCache = new Map<string, { value: CreditTransaction[]; fetchedAt: number }>()
const transactionInflight = new Map<string, Promise<CreditTransaction[]>>()

export function useCredits(userId: string | null) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [transactions, setTransactions] = useState<CreditTransaction[]>([])
  const [loading, setLoading] = useState(true)

  const fetchProfile = useCallback(async () => {
    if (!userId) { setLoading(false); return }

    // Dev bypass
    if (userId === 'dev-user-local') {
      setProfile(DEV_PROFILE)
      setLoading(false)
      return
    }

    const cached = profileCache.get(userId)
    if (cached && Date.now() - cached.fetchedAt < PROFILE_CACHE_TTL_MS) {
      setProfile(cached.value)
      setLoading(false)
      return
    }

    const existing = profileInflight.get(userId)
    if (existing) {
      const value = await existing.catch(() => null)
      if (value) setProfile({ ...value, isAdmin: value.role === 'admin' })
      setLoading(false)
      return
    }

    const request = (async () => {
      const res = await apiFetch(`${API_BASE}/api/profile/bootstrap`)
      if (!res.ok) throw new Error('bootstrap failed')
      const data = await res.json()
      if (data?.profile) {
        const nextProfile = data.profile as UserProfile
        const normalized = {
          ...nextProfile,
          isAdmin: nextProfile.role === 'admin',
        }
        profileCache.set(userId, { value: normalized, fetchedAt: Date.now() })
        return normalized
      }
      return null
    })()

    profileInflight.set(userId, request)
    try {
      const value = await request
      if (value) setProfile(value)
    } catch {
      // Fall back to existing cached profile if bootstrap is temporarily unavailable.
    } finally {
      profileInflight.delete(userId)
      setLoading(false)
    }
  }, [userId])

  const fetchTransactions = useCallback(async () => {
    if (!userId || userId === 'dev-user-local') {
      setTransactions([
        { id: '1', amount: 50, type: 'signup_bonus', description: 'Velkomstbonus ved registrering', created_at: new Date().toISOString() },
        { id: '2', amount: 1000, type: 'pro_monthly', description: 'Pro-abonnement aktivert – 1 000 kreditter', created_at: new Date().toISOString() },
      ])
      return
    }

    const cached = transactionCache.get(userId)
    if (cached && Date.now() - cached.fetchedAt < PROFILE_CACHE_TTL_MS) {
      setTransactions(cached.value)
      return
    }

    const existing = transactionInflight.get(userId)
    if (existing) {
      const value = await existing.catch(() => [])
      setTransactions(value)
      return
    }

    const request = (async () => {
      const supabase = getSupabase()
      const { data } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20)

      const next = (data as CreditTransaction[] | null) ?? []
      transactionCache.set(userId, { value: next, fetchedAt: Date.now() })
      return next
    })()

    transactionInflight.set(userId, request)
    try {
      const next = await request
      setTransactions(next)
    } catch {
      // ignore
    } finally {
      transactionInflight.delete(userId)
    }
  }, [userId])

  useEffect(() => {
    fetchProfile()
    fetchTransactions()
  }, [fetchProfile, fetchTransactions])

  // Real-time kredittoppdatering fra agent/chat events
  useEffect(() => {
    const onCreditsUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (typeof detail?.credits === 'number') {
        // Oppdater kreditter direkte uten API-kall
        setProfile(prev => prev ? { ...prev, credits: detail.credits } : prev)
      }
    }
    const onCreditsRefresh = () => {
      fetchProfile()
      fetchTransactions()
    }
    window.addEventListener('credits-updated', onCreditsUpdated)
    window.addEventListener('chat-credits-refresh', onCreditsRefresh)
    return () => {
      window.removeEventListener('credits-updated', onCreditsUpdated)
      window.removeEventListener('chat-credits-refresh', onCreditsRefresh)
    }
  }, [fetchProfile, fetchTransactions])

  const refreshCredits = useCallback(() => {
    fetchProfile()
    fetchTransactions()
  }, [fetchProfile, fetchTransactions])

  return { profile, transactions, loading, refreshCredits }
}
